import React from "react";
import {
  RequirementBlock,
  RequirementCategory,
  RequirementList,
  RequirementNote,
  RequirementOption,
  RequirementSection,
} from "../types";
import { handleBoldShortcut } from "../../utils/richText";

const OPTION_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function emptyList(): RequirementList {
  return { style: "numbered", items: [] };
}

function emptyCategory(): RequirementCategory {
  return { type: "category", title: "", kind: "list", list: emptyList() };
}

function emptyNote(): RequirementNote {
  return { type: "note", text: "" };
}

function emptySection(): RequirementSection {
  return { type: "section", title: "", items: [emptyCategory()] };
}

// A single-line text field that supports Cmd/Ctrl+B to bold the selection
// (see utils/richText.tsx) -- used for category titles, option labels, and
// individual list items, anywhere requirement text is entered.
function BoldableInput(props: {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  className?: string;
}) {
  return (
    <input
      type="text"
      value={props.value}
      placeholder={props.placeholder}
      onChange={(e) => props.onChange(e.target.value)}
      onKeyDown={(e) => handleBoldShortcut(e, props.value, props.onChange)}
      className={
        props.className ??
        "flex-1 border border-fourth-light rounded-lg px-3 py-1.5 text-sm outline-none focus:border-gold transition-colors bg-white"
      }
    />
  );
}

// Multi-line variant of BoldableInput, for note blocks.
function BoldableTextarea(props: { value: string; placeholder?: string; onChange: (value: string) => void }) {
  return (
    <textarea
      value={props.value}
      placeholder={props.placeholder}
      onChange={(e) => props.onChange(e.target.value)}
      onKeyDown={(e) => handleBoldShortcut(e, props.value, props.onChange)}
      rows={3}
      className="flex-1 border border-fourth-light rounded-lg px-3 py-1.5 text-sm outline-none focus:border-gold transition-colors bg-white resize-y"
    />
  );
}

function StyleToggle(props: { style: "numbered" | "plain"; onChange: (style: "numbered" | "plain") => void }) {
  return (
    <div className="flex gap-1 text-xs shrink-0">
      {(["numbered", "plain"] as const).map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => props.onChange(s)}
          className={`px-2 py-1 rounded-md border transition-colors ${
            props.style === s
              ? "bg-secondary-dark text-white border-secondary-dark"
              : "bg-white border-fourth-light text-secondary-dark/70 hover:border-gold"
          }`}
        >
          {s === "numbered" ? "编号列表" : "无标记列表"}
        </button>
      ))}
    </div>
  );
}

// Add/remove/reorder editor for a RequirementList's items -- shared by a
// plain category and by each option/route's own nested list.
function ListEditor(props: { list: RequirementList; onChange: (list: RequirementList) => void }) {
  const { list, onChange } = props;

  function updateItem(index: number, value: string) {
    const items = [...list.items];
    items[index] = value;
    onChange({ ...list, items });
  }

  function removeItem(index: number) {
    onChange({ ...list, items: list.items.filter((_, i) => i !== index) });
  }

  function moveItem(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= list.items.length) return;
    const items = [...list.items];
    [items[index], items[target]] = [items[target], items[index]];
    onChange({ ...list, items });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-secondary-light">条目（可 Cmd/Ctrl+B 加粗选中文字）</span>
        <StyleToggle style={list.style} onChange={(style) => onChange({ ...list, style })} />
      </div>
      <div className="flex flex-col gap-2">
        {list.items.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="text-xs text-secondary-light w-5 shrink-0 text-right">
              {list.style === "numbered" ? `${index + 1}.` : "—"}
            </span>
            <BoldableInput value={item} onChange={(v) => updateItem(index, v)} />
            <button type="button" onClick={() => moveItem(index, -1)} className="text-secondary-light hover:text-gold px-1" title="上移">↑</button>
            <button type="button" onClick={() => moveItem(index, 1)} className="text-secondary-light hover:text-gold px-1" title="下移">↓</button>
            <button type="button" onClick={() => removeItem(index)} className="text-red-400 hover:text-red-600 px-1" title="删除">✕</button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange({ ...list, items: [...list.items, ""] })}
        className="mt-2 text-xs text-gold hover:text-secondary-dark transition-colors"
      >
        + 添加一项
      </button>
    </div>
  );
}

// Editor for a category's `options` (routes A/B/C...) -- each option has its
// own short label and its own independently-styled list.
function OptionsEditor(props: { options: RequirementOption[]; onChange: (options: RequirementOption[]) => void }) {
  const { options, onChange } = props;

  function update(index: number, option: RequirementOption) {
    const next = [...options];
    next[index] = option;
    onChange(next);
  }

  function remove(index: number) {
    onChange(options.filter((_, i) => i !== index));
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= options.length) return;
    const next = [...options];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-3">
      {options.map((option, index) => (
        <div key={index} className="border border-fourth-light rounded-lg p-3 bg-white">
          <div className="flex items-start gap-2 mb-2">
            <span className="font-serif text-gold font-semibold text-sm shrink-0 mt-1.5">
              路线 {OPTION_LETTERS[index] ?? index + 1}
            </span>
            <BoldableInput
              value={option.label}
              placeholder="路线名称，如 250,000欧元投资门槛"
              onChange={(v) => update(index, { ...option, label: v })}
            />
            <div className="flex gap-1 shrink-0">
              <button type="button" onClick={() => move(index, -1)} className="text-secondary-light hover:text-gold px-1" title="上移">↑</button>
              <button type="button" onClick={() => move(index, 1)} className="text-secondary-light hover:text-gold px-1" title="下移">↓</button>
              <button type="button" onClick={() => remove(index)} className="text-red-400 hover:text-red-600 px-1" title="删除路线">✕</button>
            </div>
          </div>
          <ListEditor list={option.list} onChange={(list) => update(index, { ...option, list })} />
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...options, { label: "", list: emptyList() }])}
        className="text-xs text-gold hover:text-secondary-dark transition-colors self-start"
      >
        + 添加路线 {OPTION_LETTERS[options.length] ?? options.length + 1}
      </button>
    </div>
  );
}

function CategoryEditor(props: { category: RequirementCategory; onChange: (category: RequirementCategory) => void }) {
  const { category, onChange } = props;

  function setKind(kind: "list" | "options") {
    if (kind === category.kind) return;
    if (kind === "list") {
      onChange({ ...category, kind, list: category.list ?? emptyList(), options: undefined });
    } else {
      onChange({ ...category, kind, options: category.options ?? [], list: undefined });
    }
  }

  return (
    <div className="border border-fourth-light rounded-lg p-4 bg-c-gray/40">
      <div className="flex items-start gap-2 mb-3">
        <BoldableInput
          value={category.title}
          placeholder="分类标题，如 主申请人要求"
          onChange={(title) => onChange({ ...category, title })}
          className="flex-1 border border-fourth-light rounded-lg px-3 py-1.5 text-sm font-medium outline-none focus:border-gold transition-colors bg-white"
        />
      </div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-secondary-light shrink-0">分类内容：</span>
        <div className="flex gap-1 text-xs">
          {(["list", "options"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`px-2.5 py-1 rounded-md border transition-colors ${
                category.kind === k
                  ? "bg-secondary-dark text-white border-secondary-dark"
                  : "bg-white border-fourth-light text-secondary-dark/70 hover:border-gold"
              }`}
            >
              {k === "list" ? "列表" : "路线选项 (A/B/C)"}
            </button>
          ))}
        </div>
      </div>
      {category.kind === "list" ? (
        <ListEditor list={category.list ?? emptyList()} onChange={(list) => onChange({ ...category, list })} />
      ) : (
        <OptionsEditor
          options={category.options ?? []}
          onChange={(options) => onChange({ ...category, options })}
        />
      )}
    </div>
  );
}

// Editor for a "大分类" (section) -- an umbrella heading wrapping several
// full categories (and, at the same level, standalone notes), each category
// still with its own title and its own list/options choice. One level up
// from CategoryEditor, reusing it (and NoteEditor) for each child.
function SectionEditor(props: { section: RequirementSection; onChange: (section: RequirementSection) => void }) {
  const { section, onChange } = props;
  // Defensive against a pre-rename "categories" shape (or any other
  // malformed/missing field) lingering in already-saved data -- falling back
  // to [] here means a bad row degrades to an empty section instead of
  // crashing the whole edit page.
  const items = section.items ?? [];

  function updateItem(index: number, item: RequirementCategory | RequirementNote) {
    const next = [...items];
    next[index] = item;
    onChange({ ...section, items: next });
  }

  function removeItem(index: number) {
    onChange({ ...section, items: items.filter((_, i) => i !== index) });
  }

  function moveItem(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange({ ...section, items: next });
  }

  return (
    <div className="border-2 border-gold/30 rounded-xl p-4 bg-white">
      <BoldableInput
        value={section.title}
        placeholder="大分类标题，如 境外投资途径"
        onChange={(title) => onChange({ ...section, title })}
        className="w-full border border-fourth-light rounded-lg px-3 py-1.5 text-sm font-semibold outline-none focus:border-gold transition-colors bg-white mb-3"
      />
      <div className="flex flex-col gap-3 pl-3 border-l-2 border-gold/20">
        {items.map((item, index) => (
          <div key={index} className="relative">
            <div className="flex justify-end gap-1 mb-1">
              <button type="button" onClick={() => moveItem(index, -1)} className="text-secondary-light hover:text-gold px-1 text-xs" title="上移">↑</button>
              <button type="button" onClick={() => moveItem(index, 1)} className="text-secondary-light hover:text-gold px-1 text-xs" title="下移">↓</button>
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="text-red-400 hover:text-red-600 px-1 text-xs"
                title={item.type === "category" ? "删除子分类" : "删除备注"}
              >
                ✕ 删除{item.type === "category" ? "子分类" : "备注"}
              </button>
            </div>
            {item.type === "category" ? (
              <CategoryEditor category={item} onChange={(c) => updateItem(index, c)} />
            ) : (
              <NoteEditor note={item} onChange={(n) => updateItem(index, n)} />
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-4">
        <button
          type="button"
          onClick={() => onChange({ ...section, items: [...items, emptyCategory()] })}
          className="text-xs text-gold hover:text-secondary-dark transition-colors"
        >
          + 添加子分类
        </button>
        <button
          type="button"
          onClick={() => onChange({ ...section, items: [...items, emptyNote()] })}
          className="text-xs text-gold hover:text-secondary-dark transition-colors"
        >
          + 添加纯文字备注
        </button>
      </div>
    </div>
  );
}

function NoteEditor(props: { note: RequirementNote; onChange: (note: RequirementNote) => void }) {
  return (
    <div className="border border-dashed border-fourth-light rounded-lg p-4 bg-white">
      <span className="block text-xs text-secondary-light mb-1.5">纯文字备注（无分类标题，可 Cmd/Ctrl+B 加粗）</span>
      <BoldableTextarea
        value={props.note.text}
        placeholder="例如：以上要求以最新官方政策为准。"
        onChange={(text) => props.onChange({ ...props.note, text })}
      />
    </div>
  );
}

function blockDeleteLabel(block: RequirementBlock): string {
  if (block.type === "category") return "分类";
  if (block.type === "section") return "大分类";
  return "备注";
}

// Add/remove/reorder editor for `requirementGroups` -- an ordered mix of
// named categories (each a plain list, or a set of auto-lettered routes
// A/B/C, each route with its own independently-styled list), sections (an
// umbrella heading wrapping several categories), and standalone notes
// (freeform text, no title). Replaces the old fixed {title, items}[] shape
// so a category like "投资要求" can express multiple investment routes
// instead of flattening them into one paragraph-per-item list.
export default function RequirementGroupsEditor(props: {
  values: RequirementBlock[];
  onChange: (values: RequirementBlock[]) => void;
}) {
  const { values, onChange } = props;

  function update(index: number, block: RequirementBlock) {
    const next = [...values];
    next[index] = block;
    onChange(next);
  }

  function remove(index: number) {
    onChange(values.filter((_, i) => i !== index));
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= values.length) return;
    const next = [...values];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  }

  return (
    <div className="mb-4">
      <div className="flex flex-col gap-4">
        {values.map((block, index) => (
          <div key={index} className="relative">
            <div className="flex justify-end gap-1 mb-1">
              <button type="button" onClick={() => move(index, -1)} className="text-secondary-light hover:text-gold px-1 text-xs" title="上移">↑</button>
              <button type="button" onClick={() => move(index, 1)} className="text-secondary-light hover:text-gold px-1 text-xs" title="下移">↓</button>
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-red-400 hover:text-red-600 px-1 text-xs"
                title={`删除${blockDeleteLabel(block)}`}
              >
                ✕ 删除{blockDeleteLabel(block)}
              </button>
            </div>
            {block.type === "category" && <CategoryEditor category={block} onChange={(b) => update(index, b)} />}
            {block.type === "section" && <SectionEditor section={block} onChange={(b) => update(index, b)} />}
            {block.type === "note" && <NoteEditor note={block} onChange={(b) => update(index, b)} />}
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-4">
        <button
          type="button"
          onClick={() => onChange([...values, emptyCategory()])}
          className="text-xs text-gold hover:text-secondary-dark transition-colors"
        >
          + 添加分类
        </button>
        <button
          type="button"
          onClick={() => onChange([...values, emptySection()])}
          className="text-xs text-gold hover:text-secondary-dark transition-colors"
        >
          + 添加大分类（包含多个子分类）
        </button>
        <button
          type="button"
          onClick={() => onChange([...values, emptyNote()])}
          className="text-xs text-gold hover:text-secondary-dark transition-colors"
        >
          + 添加纯文字备注
        </button>
      </div>
    </div>
  );
}
